import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import websocketService from '../services/websocket';
import {
  TrendingUp,
  Shield,
  Zap,
  Globe,
  ArrowRight,
  Check,
  BarChart3,
  Lock,
  Smartphone,
  HeadphonesIcon,
  Award,
  Users,
  DollarSign,
  Star,
  ChevronRight,
  Wallet,
  LineChart,
  TrendingDown,
  Activity,
  Eye,
  Layers,
  RefreshCw,
  Menu,
  X
} from 'lucide-react';

const HomePage = () => {
  const [prices, setPrices] = useState([]);
  const [activeTab, setActiveTab] = useState('spot');
  const [wsConnected, setWsConnected] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchMarkets = async () => {
      try {
        // Fetch initial market data
        const response = await api.get('/markets');
        if (response.data && mounted) {
          // data structure from controller: { tradingPairs: [...] }
          const marketsData = response.data.tradingPairs || [];

          if (!Array.isArray(marketsData)) {
            console.error('Invalid markets data format:', marketsData);
            return;
          }

          // Specific list of cryptos to display
          const displaySymbols = ['BTC', 'ETH', 'BNB', 'XRP', 'SOL', 'TRX', 'DOGE', 'LINK', 'XLM', 'XMR', 'LTC', 'SUI', 'AVAX'];

          // Transform API data to component format & Filter by displaySymbols
          const formattedMarkets = marketsData
            .filter(market => displaySymbols.includes(market.symbol.replace('USDT', '')))
            .map(market => ({
              symbol: market.symbol.replace('USDT', ''), // Display BTC instead of BTCUSDT
              pair: market.symbol,
              name: market.baseAsset || market.symbol.replace('USDT', ''),
              price: parseFloat(market.lastPrice || 0),
              change: parseFloat(market.priceChange24h || 0), // Controller uses priceChange24h, not priceChangePercent
              positive: parseFloat(market.priceChange24h || 0) >= 0,
              volume: formatVolume(market.volume24h || 0), // Controller uses volume24h
              marketCap: 'N/A'
            }))
            // Sort by the order in displaySymbols
            .sort((a, b) => displaySymbols.indexOf(a.symbol) - displaySymbols.indexOf(b.symbol));

          setPrices(formattedMarkets);

          // Connect WebSocket for live updates
          websocketService.connect();
          setWsConnected(true);
        }
      } catch (error) {
        console.error('Failed to fetch markets:', error);
      }
    };

    fetchMarkets();

    return () => {
      mounted = false;
      websocketService.disconnect();
    };
  }, []);

  // Subscribe to ticker updates when markets are loaded and WS is connected
  useEffect(() => {
    if (wsConnected && prices.length > 0) {
      const symbols = prices.map(p => p.pair);
      websocketService.subscribeToMarkets(symbols);

      const handleTicker = (data) => {
        setPrices(prevPrices => prevPrices.map(p => {
          if (p.pair === data.symbol) {
            const change = parseFloat(data.priceChangePercent);
            return {
              ...p,
              price: parseFloat(data.price || data.lastPrice || 0),
              change: change,
              positive: change >= 0,
              // Update other fields if available in ticker
            };
          }
          return p;
        }));
      };

      websocketService.on('ticker', handleTicker);

      return () => {
        websocketService.unsubscribeFromMarkets(symbols);
        websocketService.off('ticker', handleTicker);
      };
    }
  }, [wsConnected, prices.length]);

  const formatVolume = (val) => {
    const v = parseFloat(val);
    if (v >= 1000000000) return (v / 1000000000).toFixed(1) + 'B';
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
    return v.toFixed(2);
  };

  const features = [
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'Multi-signature cold wallets, SSL encryption, and 2FA protection keep your assets safe',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Zap,
      title: 'Lightning Fast Trades',
      description: 'Execute orders in milliseconds with our high-performance matching engine',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: BarChart3,
      title: 'Advanced Charts',
      description: 'Professional TradingView charts with 100+ technical indicators',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Globe,
      title: 'Global Liquidity',
      description: 'Access deep liquidity pools across 150+ countries worldwide',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Smartphone,
      title: 'Mobile Trading',
      description: 'Trade on the go with our iOS and Android apps',
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: HeadphonesIcon,
      title: '24/7 Support',
      description: 'Expert support team available round the clock in 15+ languages',
      color: 'from-red-500 to-rose-500'
    }
  ];

  const stats = [
    { value: '$2.5B+', label: 'Daily Trading Volume', icon: DollarSign },
    { value: '10M+', label: 'Active Users', icon: Users },
    { value: '150+', label: 'Countries', icon: Globe },
    { value: '0.1%', label: 'Trading Fees', icon: Award }
  ];

  const topCoins = prices.slice(0, 4);

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Day Trader',
      image: '👩‍💼',
      text: 'Best trading platform I\'ve used. Fast execution and great customer support!',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Crypto Investor',
      image: '👨‍💻',
      text: 'The security features give me peace of mind. Highly recommended!',
      rating: 5
    },
    {
      name: 'Emma Williams',
      role: 'Professional Trader',
      image: '👩‍🔬',
      text: 'Advanced charts and low fees make this my go-to exchange.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="border-b border-slate-800/50 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50 shadow-lg shadow-black/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 text-lg sm:text-2xl font-bold">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-1.5 sm:p-2 rounded-lg sm:rounded-xl">
                <TrendingUp className="text-white" size={24} />
              </div>
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Albatech
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <a href="#markets" className="text-gray-300 hover:text-white transition font-medium">Markets</a>
              <a href="#features" className="text-gray-300 hover:text-white transition font-medium">Features</a>
              <a href="#about" className="text-gray-300 hover:text-white transition font-medium">About</a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/login"
                className="text-gray-300 hover:text-white transition px-6 py-2.5 font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition font-semibold shadow-lg shadow-blue-500/30"
              >
                Get Started
              </Link>
            </div>
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-300 hover:text-white p-2"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800 animate-fade-in">
            <div className="px-4 pt-2 pb-6 space-y-4">
              <a href="#markets" className="block text-gray-300 hover:text-white py-2 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Markets</a>
              <a href="#features" className="block text-gray-300 hover:text-white py-2 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
              <a href="#about" className="block text-gray-300 hover:text-white py-2 font-medium" onClick={() => setIsMobileMenuOpen(false)}>About</a>
              <div className="pt-4 flex flex-col gap-3">
                <Link
                  to="/login"
                  className="block text-center text-gray-300 hover:text-white py-2.5 font-medium border border-slate-700 rounded-xl"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="block text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 rounded-xl font-semibold shadow-lg shadow-blue-500/30"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Price Ticker */}
      <div className="bg-slate-900/60 border-b border-slate-800/50 overflow-hidden backdrop-blur">
        <div className="flex animate-scroll py-2 sm:py-3">
          {[...prices, ...prices].map((coin, index) => (
            <div
              key={index}
              className="flex items-center gap-2 sm:gap-4 px-4 sm:px-8 whitespace-nowrap border-r border-slate-800/50"
            >
              <span className="text-gray-400 font-semibold text-xs sm:text-sm">{coin.symbol}/USDT</span>
              <span className="text-white font-bold text-sm sm:text-lg">
                ${coin.price.toFixed(2)}
              </span>
              <span className={`text-xs sm:text-sm font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${coin.positive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {coin.positive ? '+' : ''}{coin.change.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-8 sm:py-12 lg:py-32">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-48 sm:w-72 h-48 sm:h-72 bg-blue-600/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-64 sm:w-96 h-64 sm:h-96 bg-purple-600/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
                <Award className="text-blue-400" size={16} />
                <span className="text-blue-400 text-xs sm:text-sm font-semibold">Trusted by 10M+ traders worldwide</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Trade Crypto
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mt-1 sm:mt-2">
                  Like a Pro
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
                Buy, sell, and trade 100+ cryptocurrencies with industry-leading security,
                lightning-fast execution, and the lowest fees in the market.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <Link
                  to="/register"
                  className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition font-semibold text-base sm:text-lg flex items-center justify-center gap-2 shadow-xl shadow-blue-500/50"
                >
                  Start Trading Now
                  <ArrowRight className="group-hover:translate-x-1 transition" size={18} />
                </Link>

                <Link
                  to="/trade"
                  className="bg-slate-800/50 backdrop-blur text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-slate-700/50 transition font-semibold text-base sm:text-lg border border-slate-700 flex items-center justify-center gap-2"
                >
                  <Eye size={18} />
                  View Markets
                </Link>
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="bg-green-500/20 p-1 rounded-full">
                    <Check className="text-green-400" size={14} />
                  </div>
                  <span>No hidden fees</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="bg-green-500/20 p-1 rounded-full">
                    <Check className="text-green-400" size={14} />
                  </div>
                  <span>Instant deposits</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="bg-green-500/20 p-1 rounded-full">
                    <Check className="text-green-400" size={14} />
                  </div>
                  <span>24/7 trading</span>
                </div>
              </div>
            </div>

            {/* Right Content - Live Market Data */}
            <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-white">Highest Volume</h3>
                <div className="flex gap-1 sm:gap-2">
                  <button
                    onClick={() => setActiveTab('spot')}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition ${activeTab === 'spot' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    Spot
                  </button>
                  <button
                    onClick={() => setActiveTab('futures')}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition ${activeTab === 'futures' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    Futures
                  </button>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {topCoins.map((coin, index) => (
                  <div key={index} className="bg-slate-900/50 rounded-lg sm:rounded-xl p-3 sm:p-4 hover:bg-slate-900/70 transition cursor-pointer">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                          {coin.symbol[0]}
                        </div>
                        <div>
                          <div className="text-white font-semibold text-sm sm:text-base">{coin.symbol}</div>
                          <div className="text-gray-400 text-xs sm:text-sm">{coin.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold text-sm sm:text-base">${coin.price.toFixed(2)}</div>
                        <div className={`text-xs sm:text-sm font-semibold ${coin.positive ? 'text-green-400' : 'text-red-400'}`}>
                          {coin.positive ? '+' : ''}{coin.change.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Vol: {coin.volume}</span>
                      <span>MCap: ${coin.marketCap}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/trade"
                className="mt-4 sm:mt-6 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 sm:py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition font-semibold text-sm sm:text-base flex items-center justify-center gap-2"
              >
                View All Markets
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900/50 backdrop-blur border-y border-slate-800/50 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl mb-3 sm:mb-4 group-hover:scale-110 transition-transform">
                  <stat.icon className="text-white" size={20} />
                </div>
                <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1 sm:mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400 font-medium text-xs sm:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-3 sm:mb-4">
              <Star className="text-blue-400" size={16} />
              <span className="text-blue-400 text-xs sm:text-sm font-semibold">Why Choose Us</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">
              Everything You Need to
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Trade with Confidence
              </span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto">
              Experience the future of cryptocurrency trading with cutting-edge technology,
              unmatched security, and professional-grade tools
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:bg-slate-800/50 hover:border-slate-600 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity blur-2xl"
                  style={{ background: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }}></div>

                <div className={`bg-gradient-to-br ${feature.color} w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Options */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
              Multiple Ways to Trade
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400">
              Choose the trading style that suits you best
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                icon: Wallet,
                title: 'Spot Trading',
                description: 'Buy and sell cryptocurrencies instantly at current market prices',
                features: ['Instant execution', 'Low fees', '100+ pairs']
              },
              {
                icon: LineChart,
                title: 'Margin Trading',
                description: 'Amplify your trading power with up to 10x leverage',
                features: ['Up to 10x leverage', 'Risk management', 'Advanced orders']
              },
              {
                icon: Activity,
                title: 'Futures Trading',
                description: 'Trade perpetual contracts with high liquidity',
                features: ['Perpetual contracts', 'Deep liquidity', 'Hedge positions']
              }
            ].map((option, index) => (
              <div key={index} className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-blue-500/50 transition-all sm:col-span-2 lg:col-span-1 last:sm:col-span-2 last:lg:col-span-1">
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                  <option.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3">{option.title}</h3>
                <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6">{option.description}</p>
                <ul className="space-y-2">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <Check className="text-green-400" size={14} />
                      <span className="text-xs sm:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400">
              Three simple steps to begin your crypto journey
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 relative">
            {/* Connecting Lines */}
            <div className="hidden md:block absolute top-1/3 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-blue-600 to-purple-600"></div>

            {[
              {
                step: '01',
                title: 'Create Account',
                description: 'Sign up in seconds with just your email. No credit card required.',
                icon: Users
              },
              {
                step: '02',
                title: 'Verify Identity',
                description: 'Complete quick KYC verification to unlock full trading features.',
                icon: Shield
              },
              {
                step: '03',
                title: 'Start Trading',
                description: 'Deposit funds and begin trading instantly with low fees.',
                icon: TrendingUp
              }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full mb-4 sm:mb-6 relative z-10">
                  <item.icon className="text-white" size={24} />
                </div>
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-blue-600/10 absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2 sm:-translate-y-4">
                  {item.step}
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
              Trusted by Traders Worldwide
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400">
              See what our users have to say
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 sm:col-span-2 lg:col-span-1 last:sm:col-start-2 last:lg:col-start-auto">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                  ))}
                </div>
                <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="text-3xl sm:text-4xl">{testimonial.image}</div>
                  <div>
                    <div className="text-white font-semibold text-sm sm:text-base">{testimonial.name}</div>
                    <div className="text-gray-400 text-xs sm:text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="relative z-10 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
                Ready to Start Your Crypto Journey?
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8">
                Join millions of traders and investors. Start trading in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-gray-100 transition font-bold text-base sm:text-lg shadow-xl"
                >
                  Create Free Account
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/trade"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-white/20 transition font-bold text-base sm:text-lg border-2 border-white/30"
                >
                  <Eye size={18} />
                  Explore Platform
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-1.5 sm:p-2 rounded-lg sm:rounded-xl">
                  <TrendingUp className="text-white" size={24} />
                </div>
                Albatech
              </div>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 leading-relaxed">
                The world's leading cryptocurrency trading platform. Trade with confidence,
                security, and the best rates in the market.
              </p>
              <div className="flex gap-4">
                {['Twitter', 'Telegram', 'Discord'].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition"
                  >
                    <span className="text-white text-xs sm:text-sm">{social[0]}</span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Products</h4>
              <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition">Spot Trading</a></li>
                <li><a href="#" className="hover:text-white transition">Margin Trading</a></li>
                <li><a href="#" className="hover:text-white transition">Futures</a></li>
                <li><a href="#" className="hover:text-white transition">Staking</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Company</h4>
              <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition">About Us</a></li>
                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Press</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm">
                <li><Link to="/support" className="hover:text-white transition">Help Center</Link></li>
                <li><Link to="/support" className="hover:text-white transition">Contact Us</Link></li>
                <li><a href="#" className="hover:text-white transition">API Docs</a></li>
                <li><Link to="/fees" className="hover:text-white transition">Fees</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-gray-400 text-xs sm:text-sm">
              © 2025 Albatech. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
              <Link to="/risk" className="hover:text-white transition">Risk Disclosure</Link>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 40s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }

        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
